import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import type { GameRow, Sport, MarketType, DateFilter, StatusFilter, MarketSide, PriceMode, TeamSide, MultiOutcomeEvent, SeriesOutcome } from './types';
import { MULTI_OUTCOME_SPORTS, SPORT_LABEL, SPORT_TABS } from './types';

function todayMdy(): string {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

function mdyToIso(mdy: string): string {
  const [mm, dd, yyyy] = mdy.split('/');
  return `${yyyy}-${mm}-${dd}`;
}

function isoToMdy(iso: string): string {
  const [yyyy, mm, dd] = iso.split('-');
  return yyyy && mm && dd ? `${mm}/${dd}/${yyyy}` : iso;
}

function startOfIsoDayMs(iso: string): number {
  const [yyyy, mm, dd] = iso.split('-').map(Number);
  if (!yyyy || !mm || !dd) return Number.NaN;
  return new Date(yyyy, mm - 1, dd).getTime();
}

function gameSortMs(game: GameRow): number {
  if (game.startTimeMs != null && Number.isFinite(game.startTimeMs)) {
    return game.startTimeMs;
  }
  const dayMs = startOfIsoDayMs(game.date);
  return Number.isFinite(dayMs) ? dayMs : Number.POSITIVE_INFINITY;
}

const SPORT_ORDER = new Map<Sport, number>(SPORT_TABS.map((tab, index) => [tab.id, index]));

function sortSports(sports: Iterable<Sport>): Sport[] {
  return Array.from(sports).sort((a, b) => (SPORT_ORDER.get(a) ?? 999) - (SPORT_ORDER.get(b) ?? 999));
}

function sportSelectionLabel(sports: Sport[], allSports: boolean): string {
  if (allSports) return 'All Sports';
  if (sports.length === 1) return SPORT_LABEL[sports[0]] ?? sports[0].toUpperCase();
  if (sports.length === 2) return sports.map((sport) => SPORT_LABEL[sport] ?? sport.toUpperCase()).join(' + ');
  return `${sports.length} Sports`;
}

function shiftMdy(mdy: string, days: number): string {
  const [mm, dd, yyyy] = mdy.split('/').map(Number);
  const d = new Date(yyyy, mm - 1, dd);
  d.setDate(d.getDate() + days);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}
import { DashboardToolbar } from './DashboardToolbar';
import { GameTable } from './GameTable';
import { SeriesTable } from './SeriesTable';
import { MultiOutcomeTable } from './MultiOutcomeTable';
import { SpreadsTotals } from '../SpreadsTotals';
import { GameDetailPanel, type TradeIntent, type BetLineContext } from './GameDetailPanel';
import { buildMultiOutcomeBetContext, synthesizeFromMultiOutcome } from './multiOutcome';
import { bestPriceCentsForOrderSide } from './tradeModalBook';
import { PositionSidebar, PositionsWidget } from './PositionSidebar';
import { useMarketStore, useConsensusFor } from '../../stores/marketStore';
import { sportIsServable } from '../../clients/feedConfig';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useAccountStore } from '../../stores/accountStore';
import { useUserKalshiPOS } from '@/hooks/useUserKalshiPOS';
import { useEdgeBotsByTicker } from '@/hooks/useEdgeBotsByTicker';
import { useArchivedGames, type ArchivedGame } from './useArchivedGames';
import { parseTicker } from '@/lib/kalshiTicker/parse';
import { composeHydraGameKey, toHydraGameKey } from '@/services/analyticsGameKey';
import { useGameStatesPoll } from '@/hooks/useGameStatesPoll';
import { useGameStateFor } from '@/stores/gameStatesStore';
import { useFeedFreshness } from '@/hooks/useFeedFreshness';

interface ValueDashboardProps {
  /** Lets the floating positions popup open the full Position Tracker tab. */
  onOpenPositionsPage?: () => void;
}

// Activity-window for the chip-change resnap heal. Passed to
// requestResnapshotIfStale, which fires a snapshot only when the share
// of tickers with any frame in the last CHIP_RESNAP_STALE_MS falls below
// ~10% — i.e. the feed is mostly-dead, not just one chatty ticker
// keeping the global timestamp warm. 60 s window so quiet-but-healthy
// markets still register as active.
const CHIP_RESNAP_STALE_MS = 60_000;

export function ValueDashboard({ onOpenPositionsPage }: ValueDashboardProps = {}) {
  // ── Hydra data (connection managed at App level) ───────────────────
  // Feed freshness still drives the empty-state "Connecting to Hydra…" copy;
  // the stale/seconds readout moved to the global StatusBar.
  const { connected } = useFeedFreshness();
  const getGames = useMarketStore((s) => s.getGames);
  const getMultiOutcomeEvents = useMarketStore((s) => s.getMultiOutcomeEvents);
  const getAdvanceEvents = useMarketStore((s) => s.getAdvanceEvents);
  const getSeries = useMarketStore((s) => s.getSeries);
  const getActiveSports = useMarketStore((s) => s.getActiveSports);
  const requestResnapshotIfStale = useMarketStore((s) => s.requestResnapshotIfStale);
  const version = useMarketStore((s) => s.version);

  // Landing-page defaults — read once at mount time so changing the pref in
  // Settings doesn't yank the active view out from under the user.
  const initialMarketType = useDisplaySettings.getState().defaultMarketType;
  const initialPriceMode = useDisplaySettings.getState().defaultPriceMode;

  // ── Toolbar state ──────────────────────────────────────────────────
  // The sport filter lives in the URL (?sport=nba | ?sport=all; absent →
  // the user's saved default). Reload keeps you where you were, links are
  // shareable, and Back from GameDetail restores the sport natively through
  // history — this replaced the sessionStorage returnSport stash (TTL +
  // StrictMode peek/clear dance, deleted). Chip clicks write with
  // replace:true so they don't pile up history entries.
  const [searchParams, setSearchParams] = useSearchParams();
  const [defaultSport] = useState(() => useDisplaySettings.getState().defaultSport);
  const sportParam = searchParams.get('sport');
  const allSports = sportParam === 'all';
  const selectedSport: Sport =
    sportParam && SPORT_TABS.some((t) => t.id === sportParam)
      ? (sportParam as Sport)
      : defaultSport;
  // Moneyline sport filter. Empty set means "All sports" (?sport=all);
  // non-empty means the single active moneyline sport. Futures and
  // spread/total are single-sport and read selectedSport — which falls back
  // to the saved default while 'all' is active.
  const selectedSports = useMemo<ReadonlySet<Sport>>(
    () => (allSports ? new Set<Sport>() : new Set([selectedSport])),
    [allSports, selectedSport],
  );
  const [selectedMarketType, setSelectedMarketType] = useState<MarketType>(initialMarketType);
  const [selectedDate, setSelectedDate] = useState(todayMdy);
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');
  // Default 'all' so FINAL rows show in the natural sort during their
  // ~5-min hydra cache lifetime. The 'active' / 'settled' refinements stay
  // available on the toolbar for users who want a filtered view.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [marketSide, setMarketSide] = useState<MarketSide>('no');
  const [priceMode, setPriceMode] = useState<PriceMode>(initialPriceMode);
  const selectedDateIso = useMemo(() => mdyToIso(selectedDate), [selectedDate]);
  const selectedSportsKey = useMemo(
    () => sortSports(selectedSports).join('|'),
    [selectedSports],
  );
  const showAllSportsChip = selectedMarketType === 'moneyline';
  const selectedMoneylineSports = useMemo<Sport[]>(() => {
    const sports =
      selectedSports.size === 0
        ? SPORT_TABS.map((tab) => tab.id)
        : sortSports(selectedSports);
    return sports.filter((sport) => !MULTI_OUTCOME_SPORTS.has(sport));
  }, [selectedSports, selectedSportsKey]);
  const singleSelectedMultiOutcomeSport = useMemo<Sport | null>(() => {
    if (selectedMarketType !== 'moneyline' || selectedSports.size !== 1) return null;
    const [sport] = Array.from(selectedSports);
    return MULTI_OUTCOME_SPORTS.has(sport) ? sport : null;
  }, [selectedMarketType, selectedSports, selectedSportsKey]);

  // Heal stale state on any toolbar interaction. Skips on first render
  // (the connect-time snapshot covers initial load) and is a no-op
  // unless ~90% of tickers have gone silent in the last
  // CHIP_RESNAP_STALE_MS — guards against the failure mode where one
  // chatty ticker keeps the connection looking alive while everything
  // else is throttled or upstream-dead. Pure-display toggles like
  // marketSide / priceMode don't need new data, but firing the check on
  // them is essentially free and catches "I sat idle, came back, clicked
  // anything" stale paths.
  const firstToolbarRender = useRef(true);
  useEffect(() => {
    if (firstToolbarRender.current) {
      firstToolbarRender.current = false;
      return;
    }
    requestResnapshotIfStale(CHIP_RESNAP_STALE_MS);
  }, [
    selectedSport,
    selectedSportsKey,
    selectedMarketType,
    selectedDate,
    dateFilter,
    statusFilter,
    marketSide,
    priceMode,
    requestResnapshotIfStale,
  ]);

  // ── Position sidebar state ─────────────────────────────────────────
  const [showPositionSidebar, setShowPositionSidebar] = useState(false);

  const selectedMoneylineSportSet = useMemo(
    () => new Set(selectedMoneylineSports),
    [selectedMoneylineSports],
  );
  const selectedMoneylineLabelSports = useMemo(
    () => (selectedSports.size === 0 ? selectedMoneylineSports : sortSports(selectedSports)),
    [selectedMoneylineSports, selectedSports, selectedSportsKey],
  );
  const sportFilterLabel =
    selectedMarketType === 'moneyline'
      ? sportSelectionLabel(selectedMoneylineLabelSports, selectedSports.size === 0)
      : SPORT_LABEL[selectedSport] ?? selectedSport.toUpperCase();
  const tableTitleLabel =
    selectedMarketType === 'moneyline'
      ? `${sportFilterLabel} Moneylines`
      : undefined;

  // ── Data from Hydra via Zustand ─────────────────────────────────────
  // version changes on every RAF tick → forces fresh derivation from mutable maps.
  // Series rows merge cross-venue via canonicalSeriesKey inside marketStore;
  // no client-side merge needed.
  const liveGamesForFilter = useMemo(() => {
    void version; // explicit dependency
    if (selectedMarketType === 'series') return [];
    const sports =
      selectedMarketType === 'moneyline' ? selectedMoneylineSports : [selectedSport];
    return sports.flatMap((sport) => getGames(sport, selectedMarketType, {
      statusFilter,
      dateFilter,
      selectedDateIso,
    }));
  }, [
    getGames,
    selectedSport,
    selectedMoneylineSports,
    selectedMarketType,
    version,
    statusFilter,
    dateFilter,
    selectedDateIso,
  ]);

  const upcomingFallbackGames = useMemo(() => {
    void version;
    if (selectedMarketType === 'series' || dateFilter !== 'today' || liveGamesForFilter.length > 0) {
      return [];
    }
    const dayStartMs = startOfIsoDayMs(selectedDateIso);
    if (!Number.isFinite(dayStartMs)) return [];
    const sports =
      selectedMarketType === 'moneyline' ? selectedMoneylineSports : [selectedSport];
    // All upcoming games for the selected sport(s), excluding final/series/archived.
    const upcoming = sports.flatMap((sport) => getGames(sport, selectedMarketType, {
      statusFilter,
      dateFilter: 'all',
      selectedDateIso,
    })).filter((game) => {
      if (game.series || game.archived || game.gameState === 'final') return false;
      return gameSortMs(game) >= dayStartMs;
    });
    if (upcoming.length === 0) return [];
    // Show ONLY the next calendar day that has games — whether that's tomorrow
    // or two months out (off-season / sparse sport). Stop on the first day with
    // games: a dense daily sport (baseball) then shows one day's slate instead
    // of a 7-day pile, and a no-games-today "Today" still isn't a dead end.
    const nextDate = upcoming.reduce(
      (soonest, game) => (game.date < soonest ? game.date : soonest),
      upcoming[0].date,
    );
    return upcoming
      .filter((game) => game.date === nextDate)
      .sort((a, b) => {
        const startCmp = gameSortMs(a) - gameSortMs(b);
        if (startCmp !== 0) return startCmp;
        return a.awayCode.localeCompare(b.awayCode);
      });
  }, [
    getGames,
    selectedSport,
    selectedMoneylineSports,
    selectedMarketType,
    version,
    statusFilter,
    dateFilter,
    selectedDateIso,
    liveGamesForFilter.length,
  ]);

  const usingUpcomingFallback = dateFilter === 'today'
    && liveGamesForFilter.length === 0
    && upcomingFallbackGames.length > 0;
  const liveGames = usingUpcomingFallback ? upcomingFallbackGames : liveGamesForFilter;
  // The single date the fallback landed on (all fallback rows share it) — drives
  // the empty-state copy. null when not in fallback.
  const upcomingFallbackDate = usingUpcomingFallback
    ? upcomingFallbackGames[0]?.date ?? null
    : null;

  // Closed games sourced from the analytics endpoint (Postgres-backed,
  // 30-day lookback). Skipped on the series tab and on non-moneyline
  // market types — analytics endpoint returns moneyline-shaped summaries
  // per game; spread/total surfaces aren't a fit for the skinny-row
  // projection. `statusFilter='active'` also short-circuits since
  // archived rows are by definition non-active.
  const { archived: archivedRaw, loading: archivedLoading } = useArchivedGames(
    selectedMarketType === 'moneyline' && statusFilter !== 'active'
      ? selectedDateIso
      : null,
  );

  const games = useMemo<GameRow[]>(() => {
    // Any FINAL row from the live store gets collapsed into the skinny
    // archived shape so finished games render the same one-liner
    // regardless of whether they came from `_markets` (still in
    // session-cached hydra state) or the analytics fetch. WNBA was the
    // visible offender — its games sit in `_markets` longer than other
    // sports' do post-close, so they kept rendering as full live rows.
    const liveTransformed = liveGames.map((g) =>
      g.gameState === 'final' && !g.archived
        ? {
            ...g,
            archived: {
              kalshiVolume: g.kalshi?.away.volume ?? 0,
              polyVolume: g.polymarket?.away.volume ?? 0,
            },
          }
        : g,
    );
    if (archivedRaw.length === 0) return liveTransformed;
    // Dedupe: drop any analytics row whose canonical gameKey already
    // appears in the live set (live data wins). Live rows use the
    // marketStore's own key shape; normalize through toHydraGameKey so
    // the comparison survives the sport-prefix vs no-prefix variants.
    const liveCanonicalKeys = new Set<string>();
    for (const g of liveTransformed) {
      const k = toHydraGameKey(g.key);
      if (k) liveCanonicalKeys.add(k);
    }
    const archivedRows: GameRow[] = [];
    for (const a of archivedRaw) {
      const sport = a.sport as Sport;
      if (selectedMarketType === 'moneyline') {
        if (!selectedMoneylineSportSet.has(sport)) continue;
      } else if (sport !== selectedSport) continue;
      if (liveCanonicalKeys.has(a.gameKey)) continue;
      archivedRows.push(archivedToGameRow(a));
    }
    if (archivedRows.length === 0) return liveTransformed;
    // Sort archived rows by date desc so newest closed games sit
    // immediately under the live block.
    archivedRows.sort((x, y) => (x.date < y.date ? 1 : x.date > y.date ? -1 : 0));
    return [...liveTransformed, ...archivedRows];
  }, [
    liveGames,
    archivedRaw,
    selectedSport,
    selectedMarketType,
    selectedMoneylineSportSet,
  ]);

  const seriesEvent = useMemo(() => {
    void version;
    if (selectedMarketType !== 'series') return null;
    return getSeries(selectedSport);
  }, [getSeries, selectedSport, selectedMarketType, version]);

  // Multi-outcome (3-way) sports — soccer Home/Away/Draw. The toolbar's
  // date/status filters apply exactly like the binary table's, including
  // the 7-day upcoming fallback when the selected day has no games.
  const multiOutcomeSport = singleSelectedMultiOutcomeSport;
  const isMultiOutcomeSport = multiOutcomeSport != null;
  const multiEventsForFilter = useMemo(() => {
    void version;
    if (!multiOutcomeSport) return [];
    return getMultiOutcomeEvents(multiOutcomeSport, { statusFilter, dateFilter, selectedDateIso });
  }, [getMultiOutcomeEvents, multiOutcomeSport, version, statusFilter, dateFilter, selectedDateIso]);
  const upcomingFallbackMultiEvents = useMemo(() => {
    void version;
    if (!multiOutcomeSport || dateFilter !== 'today' || multiEventsForFilter.length > 0) {
      return [];
    }
    const dayStartMs = startOfIsoDayMs(selectedDateIso);
    if (!Number.isFinite(dayStartMs)) return [];
    const eventMs = (e: MultiOutcomeEvent) =>
      e.startTimeMs ?? (e.date ? startOfIsoDayMs(e.date) : Number.POSITIVE_INFINITY);
    const upcoming = getMultiOutcomeEvents(multiOutcomeSport, { statusFilter, dateFilter: 'all' })
      .filter((e) => e.gameState !== 'final' && eventMs(e) >= dayStartMs);
    if (upcoming.length === 0) return [];
    // Same rule as the binary fallback: only the next calendar day that has
    // events. Events without a date can't be placed on a day, so they're
    // excluded from the single-day preview.
    let nextDate: string | null = null;
    for (const e of upcoming) {
      if (e.date && (nextDate === null || e.date < nextDate)) nextDate = e.date;
    }
    if (nextDate === null) return [];
    return upcoming.filter((e) => e.date === nextDate);
  }, [
    getMultiOutcomeEvents,
    multiOutcomeSport,
    version,
    statusFilter,
    dateFilter,
    selectedDateIso,
    multiEventsForFilter.length,
  ]);
  const usingMultiUpcomingFallback =
    dateFilter === 'today' &&
    multiEventsForFilter.length === 0 &&
    upcomingFallbackMultiEvents.length > 0;

  // Knockout "To Advance" swap: a knockout game's headline moneyline is the
  // 2-way "who advances" market, not the regulation 1X2. getAdvanceEvents keys
  // its events identically to the 1X2, so swap them in place — group-stage games
  // (no advance market) keep their 3-way unchanged. Everything downstream
  // (rendering, trade clicks, orderbook/POS needs, the detail panel) reads
  // `multiEvents`, so the swap covers them all. See HYDRA_API.md "Soccer To Advance".
  const advanceEventsByKey = useMemo(() => {
    void version;
    const m = new Map<string, MultiOutcomeEvent>();
    if (!multiOutcomeSport) return m;
    for (const e of getAdvanceEvents(multiOutcomeSport)) m.set(e.key, e);
    return m;
  }, [getAdvanceEvents, multiOutcomeSport, version]);
  const baseMultiEvents = usingMultiUpcomingFallback ? upcomingFallbackMultiEvents : multiEventsForFilter;
  const multiEvents = useMemo(
    () =>
      advanceEventsByKey.size === 0
        ? baseMultiEvents
        : baseMultiEvents.map((e) => advanceEventsByKey.get(e.key) ?? e),
    [baseMultiEvents, advanceEventsByKey],
  );
  // The single date the multi fallback landed on — drives the empty-state copy.
  const multiUpcomingFallbackDate = usingMultiUpcomingFallback
    ? upcomingFallbackMultiEvents[0]?.date ?? null
    : null;

  // Sports with ≥1 live market, per hydra's activeSports meta frame.
  // Toolbar uses this to auto-hide chips for sports with no activity today.
  // Always include the currently-selected sport so the active chip doesn't
  // vanish mid-interaction even before the first frame for that sport
  // arrives (and so users can navigate back to it).
  const activeSports = useMemo(() => {
    void version;
    const set = getActiveSports();
    for (const sport of MULTI_OUTCOME_SPORTS) {
      if (getMultiOutcomeEvents(sport).length > 0) set.add(sport);
    }
    set.add(selectedSport);
    for (const sport of selectedSports) set.add(sport);
    // Drop edge-only sports (NFL) that this deploy can't actually serve. Prod
    // hydra's activeSports meta frame advertises NFL, but on a firehose-only
    // lane (no edge configured) no NFL game data arrives — so the chip would be
    // a dead end. Keep the explicitly-selected sport so a deep-link still works.
    for (const sport of [...set]) {
      if (sport !== selectedSport && !sportIsServable(sport)) set.delete(sport);
    }
    return set;
  }, [getActiveSports, getMultiOutcomeEvents, version, selectedSport, selectedSports]);

  // ── Orderbook needs ────────────────────────────────────────────────
  // Declare the set of tickers this view currently renders depth for.
  // The store unions these with other views' needs and hands the union
  // to HydraClient, which narrows the upstream orderbooks subscription.
  // Recompute from `games` (and the series outcomes when that tab's
  // active); `version` already triggers that memo upstream.
  const orderbookTickers = useMemo(() => {
    const set = new Set<string>();
    for (const g of games) {
      if (g.kalshi?.away.marketTicker) set.add(g.kalshi.away.marketTicker);
      if (g.kalshi?.home.marketTicker) set.add(g.kalshi.home.marketTicker);
      if (g.polymarket?.away.marketTicker) set.add(g.polymarket.away.marketTicker);
      if (g.polymarket?.home.marketTicker) set.add(g.polymarket.home.marketTicker);
    }
    if (seriesEvent) {
      for (const outcome of seriesEvent.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
      }
    }
    for (const event of multiEvents) {
      for (const outcome of event.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
        if (outcome.polymarket?.marketTicker) set.add(outcome.polymarket.marketTicker);
      }
    }
    return Array.from(set);
  }, [games, seriesEvent, multiEvents]);

  // Stable key derived from the ticker set so the effect only re-runs when
  // the actual set changes, not on every RAF tick (orderbookTickers gets a
  // fresh array ref every tick because `games` does).
  const orderbookTickersKey = useMemo(
    () => orderbookTickers.slice().sort().join('|'),
    [orderbookTickers],
  );

  // Kalshi-only ticker subset for the per-user POS hook. We only need
  // queue-position lookups against tickers actually shown on screen — feeding
  // the full set keeps the queue-positions REST call narrow and avoids
  // pulling depth for off-screen orders (e.g. NHL while NBA is selected).
  const kalshiVisibleTickers = useMemo(() => {
    const set = new Set<string>();
    for (const g of games) {
      if (g.kalshi?.away.marketTicker) set.add(g.kalshi.away.marketTicker);
      if (g.kalshi?.home.marketTicker) set.add(g.kalshi.home.marketTicker);
    }
    for (const event of multiEvents) {
      for (const outcome of event.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
      }
    }
    return Array.from(set);
  }, [games, multiEvents]);
  const userActivity = useUserKalshiPOS(kalshiVisibleTickers);
  // Edge bot presence by ticker (row chips). No-op fetch for non-bot users.
  const edgeBots = useEdgeBotsByTicker();

  // Canonical gameKeys the user has any position in — drives the archived-row
  // left-border highlight. Sourced from the FULL per-user position list
  // (accountStore.positions), NOT the ticker-filtered /me/snapshot poller: the
  // highlight must light archived/past-date rows for ANY game the user holds,
  // even ones with no live visible ticker, and the snapshot poller now only
  // returns the visible-ticker subset. Each ticker is parsed via parseTicker
  // (venue-agnostic so PM-* tickers resolve to the same {game, date} shape),
  // and composed into the same alpha-sorted canonical key the analytics
  // endpoint emits. Recomputes only when the position set changes.
  const accountPositions = useAccountStore((s) => s.positions);
  const positionGameKeys = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const p of accountPositions) {
      const parsed = parseTicker(p.ticker);
      if (!parsed.game || !parsed.date) continue;
      const k = composeHydraGameKey({
        date: parsed.date,
        teamA: parsed.game.awayCode,
        teamB: parsed.game.homeCode,
      });
      if (k) set.add(k);
    }
    return set;
  }, [accountPositions]);
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('value-dashboard', orderbookTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('value-dashboard', []);
    };
    // orderbookTickers is intentionally excluded — orderbookTickersKey is the
    // stable identity for the set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderbookTickersKey]);

  // Batched Unabated gameState poll for the visible game list. Drives
  // live scores in the team-name column + (next step) "finished games
  // drop to bottom" via authoritative statusId rather than the existing
  // status/time/price-collapse heuristics. Falls out cleanly when the
  // game list is empty (poller skips fetches with no keys).
  const dashboardGameKeys = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const g of games) {
      const k = toHydraGameKey(g.key);
      if (k) set.add(k);
    }
    // Multi-outcome (soccer) events too — their pair rows show live scores
    // when Unabated covers the match; misses just render the live dash.
    for (const e of multiEvents) {
      const k = toHydraGameKey(e.key);
      if (k) set.add(k);
    }
    return Array.from(set);
  }, [games, multiEvents]);
  useGameStatesPoll(dashboardGameKeys);

  // ── Detail panel state ──────────────────────────────────────────────
  const [expandedGameKey, setExpandedGameKey] = useState<string | null>(null);
  const [tradeIntent, setTradeIntent] = useState<TradeIntent | null>(null);

  // ── Spreads & Totals detail panel state ────────────────────────────
  // When the user expands a spread or total row, SpreadsTotals synthesizes
  // a GameRow for that line and feeds it here; the existing detail panel
  // renders against the synth. `stCloseSignal` bumps when the user closes
  // the panel so SpreadsTotals knows to collapse its inline expansion.
  const [spreadTotalGame, setSpreadTotalGame] = useState<GameRow | null>(null);
  const [spreadTotalBetCtx, setSpreadTotalBetCtx] = useState<BetLineContext | null>(null);
  // Leaving the spread/total tab → drop the synth + chip-strip context.
  // Without this, a totals synth set earlier still drives the side panel
  // on the moneyline tab (expandedGame's null-key path falls back to
  // spreadTotalGame). The panel ends up "stuck" on the prior totals bet
  // until the user explicitly clicks a moneyline pill.
  useEffect(() => {
    if (selectedMarketType !== 'spread-total') {
      setSpreadTotalGame(null);
      setSpreadTotalBetCtx(null);
    }
  }, [selectedMarketType]);
  const [stCloseSignal, setStCloseSignal] = useState(0);

  const handleToggleExpand = useCallback(
    (key: string, side: TeamSide) => {
      if (expandedGameKey === key) {
        if (tradeIntent?.side === side) {
          setExpandedGameKey(null);
          setTradeIntent(null);
        } else {
          setTradeIntent({ side, venue: 'kalshi' });
        }
        return;
      }
      setExpandedGameKey(key);
      setTradeIntent({ side, venue: 'kalshi' });
    },
    [expandedGameKey, tradeIntent?.side],
  );

  const handleCloseDetail = useCallback(() => {
    setExpandedGameKey(null);
    setTradeIntent(null);
    setSpreadTotalGame(null);
    setSpreadTotalBetCtx(null);
    setStCloseSignal((c) => c + 1);
  }, []);

  const handleTradeClick = useCallback(
    (
      gameKey: string,
      side: 'away' | 'home',
      venue: 'kalshi' | 'polymarket',
      opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
    ) => {
      const game = games.find((g) => g.key === gameKey);
      if (!game) return;

      const initialOrderSide = opts?.initialOrderSide ?? marketSide;
      const book =
        side === 'away'
          ? (venue === 'kalshi' ? game.kalshi?.away : game.polymarket?.away)
          : (venue === 'kalshi' ? game.kalshi?.home : game.polymarket?.home);
      const prefillPrice =
        opts?.priceCents ?? bestPriceCentsForOrderSide(book, initialOrderSide);

      // Trade modal is deprecated (see TradeModal.tsx header) — the
      // GameDetailPanel side panel is the single trade-ticket surface
      // now. setExpandedGameKey opens the panel; tradeIntent carries the
      // click's pre-fill data so the ticket lands ready-to-submit.
      //
      // Clearing spread/total state on a moneyline-pill click is
      // belt-and-suspenders: even though `expandedGame` already prefers
      // the moneyline game when expandedGameKey resolves, the panel's
      // betLineContext check is identity-based and any stale
      // spreadTotalGame ref hanging around made the totals panel
      // "stick" after the user moved to a moneyline bet. Wipe both so
      // the panel switches cleanly into moneyline mode.
      setSpreadTotalGame(null);
      setSpreadTotalBetCtx(null);
      setExpandedGameKey(gameKey);
      setTradeIntent({
        side,
        venue,
        prefillPrice,
        initialOrderSide,
        postOnly: opts?.postOnly ?? false,
      });
      // Pill click also flips the page-level YES/NO toggle to match.
      // Default is NO, so this only triggers when a trader clicks a
      // green YES pill — the table + panel then read consistently
      // from the toolbar's YES state instead of asking the user to
      // toggle separately.
      if (opts?.initialOrderSide && opts.initialOrderSide !== marketSide) {
        setMarketSide(opts.initialOrderSide);
      }
    },
    [games, marketSide],
  );

  const handleMultiOutcomeClick = useCallback(
    (
      event: MultiOutcomeEvent,
      outcome: SeriesOutcome,
      venue: 'kalshi' | 'polymarket',
      opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
    ) => {
      const game = synthesizeFromMultiOutcome(event, outcome);
      // Row click (no pill opts) on the already-open outcome toggles it
      // closed — mirrors handleToggleExpand on the binary table.
      if (!opts && expandedGameKey === game.key) {
        handleCloseDetail();
        return;
      }
      const book = venue === 'kalshi' ? outcome.kalshi : outcome.polymarket;
      // Same side semantics as the binary table: a row expand inherits the
      // toolbar's current YES/NO; only an explicit Y/N pill (or ladder)
      // click carries its own side. (The old card-grid UI defaulted row
      // clicks to YES and flipped the toolbar — jarring once soccer rows
      // looked and behaved like every other game row.)
      const initialOrderSide: MarketSide = opts?.initialOrderSide ?? marketSide;
      const prefillPrice =
        opts?.priceCents ?? bestPriceCentsForOrderSide(book ?? undefined, initialOrderSide);
      setSpreadTotalGame(game);
      setSpreadTotalBetCtx(buildMultiOutcomeBetContext(event, outcome));
      setExpandedGameKey(game.key);
      setTradeIntent({
        side: 'away',
        venue,
        prefillPrice,
        initialOrderSide,
        postOnly: opts?.postOnly ?? false,
        gameKey: game.key,
      });
      // Toolbar follows explicit pill clicks only — same rule as
      // handleTradeClick on the binary table.
      if (opts?.initialOrderSide && opts.initialOrderSide !== marketSide) {
        setMarketSide(opts.initialOrderSide);
      }
    },
    [marketSide, expandedGameKey, handleCloseDetail],
  );

  const expandedGame = useMemo(() => {
    if (expandedGameKey) {
      // expandedGameKey may be stale across market-type switches (e.g.
      // user clicked a moneyline row, then switched to spread/total
      // tab — the moneyline key won't appear in the new `games` list).
      // Don't return null in that case; fall through to spreadTotalGame
      // so the panel still opens for a fresh spread/total row click.
      const fromGames = games.find((g) => g.key === expandedGameKey);
      if (fromGames) return fromGames;
      // Multi-outcome (soccer) synth keys aren't in `games` — re-derive the
      // synth fresh from the live event each tick so the panel's books track
      // the feed (the panel renders straight off the game prop; the stored
      // spreadTotalGame below is a click-time snapshot). Falls through to the
      // snapshot when the event leaves the store (e.g. post-settle prune).
      const sep = expandedGameKey.indexOf('|multi|');
      if (sep >= 0) {
        const eventKey = expandedGameKey.slice(0, sep);
        const code = expandedGameKey.slice(sep + '|multi|'.length);
        const event = multiEvents.find((e) => e.key === eventKey);
        const outcome = event?.outcomes.find((o) => o.code === code);
        if (event && outcome) return synthesizeFromMultiOutcome(event, outcome);
      }
    }
    // SpreadsTotals synthesizes a GameRow for the active expanded line
    // and pushes it here via onActiveGameChange. Same detail panel
    // renders against either source.
    return spreadTotalGame;
  }, [expandedGameKey, games, spreadTotalGame, multiEvents]);

  const seriesTicker = useMemo(() => {
    if (selectedMarketType === 'moneyline' && selectedSports.size !== 1) {
      return '';
    }
    const map: Record<string, string> = {
      nba: 'KXNBAGAME',
      wnba: 'KXWNBAGAME',
      nhl: 'KXNHLGAME',
      cbb: 'KXNCAAMBGAME',
      cfb: 'KXNCAAFGAME',
      atp: 'KXATPMATCH / KXITFMATCH',
      wta: 'KXWTAMATCH / KXITFWMATCH',
      mlb: 'KXMLBGAME',
      ufc: 'KXUFCFIGHT',
      esports: 'KXCS2GAME / KXDOTA2GAME / KXVALORANTGAME / KXLOLGAME',
      boxing: 'KXBOXING',
      cricket: 'KXWT20MATCH',
      soccer: 'KXWCGAME / KXUECLGAME / KXINTLFRIENDLYGAME',
    };
    return map[selectedSport] ?? '';
  }, [selectedSport, selectedMarketType, selectedSports.size]);

  // ── Date navigation ─────────────────────────────────────────────────
  // The day prev/next buttons always force single-day mode; navigating a
  // date while filtered to "all" is meaningless (the filter ignores the
  // date entirely).
  const handleDatePrev = useCallback(() => {
    setSelectedDate((d) => shiftMdy(d, -1));
    setDateFilter('today');
  }, []);

  const handleDateNext = useCallback(() => {
    setSelectedDate((d) => shiftMdy(d, 1));
    setDateFilter('today');
  }, []);

  // "Today" jumps the calendar back to the actual current day AND
  // enforces single-day mode, regardless of where the user has
  // navigated.
  const handleTodayClick = useCallback(() => {
    setSelectedDate(todayMdy());
    setDateFilter('today');
  }, []);

  const handleSportToggle = useCallback(
    (sport: Sport) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('sport', sport);
          return next;
        },
        { replace: true },
      );
      // A sport switch is a context switch: close the detail panel and
      // collapse the expanded row instead of letting click-time state
      // linger. Without this, a soccer panel survived onto the next sport
      // via the spreadTotalGame fallback (frozen data), and a binary row
      // silently re-expanded when you returned to its sport — and the
      // still-mounted GameDetailPanel paid per-tick renders + held its
      // orderbook subs right through the switch's snapshot burst.
      if (sport !== selectedSport || allSports) {
        handleCloseDetail();
      }
      // Multi-outcome sports (soccer) now have BOTH the 3-way moneyline and the
      // full-game spread/total surfaces, but no futures/series table — so only
      // yank the tab back to moneyline when leaving a 'series' selection that
      // would blank for soccer. Moneyline and spread-total both render, so they
      // carry over on switch, mirroring the binary sports.
      if (MULTI_OUTCOME_SPORTS.has(sport) && selectedMarketType === 'series') {
        setSelectedMarketType('moneyline');
      }
    },
    [setSearchParams, selectedSport, allSports, handleCloseDetail, selectedMarketType],
  );

  const handleAllSportsClick = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sport', 'all');
        return next;
      },
      { replace: true },
    );
    if (!allSports) handleCloseDetail();
  }, [setSearchParams, allSports, handleCloseDetail]);

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar row — DashboardToolbar grows to fill the table column;
          PositionsWidget is a fixed w-96 container occupying exactly the
          right column above the order panel (same width, same left
          divider, same right edge). */}
      <div className="border-border flex items-stretch border-b">
        <div className="min-w-0 flex-1 p-3">
          <DashboardToolbar
            selectedSport={selectedSport}
            selectedSports={selectedSports}
            activeSports={activeSports}
            selectedMarketType={selectedMarketType}
            selectedDate={selectedDate}
            isToday={dateFilter === 'today' && selectedDate === todayMdy()}
            dateFilter={dateFilter}
            statusFilter={statusFilter}
            marketSide={marketSide}
            priceMode={priceMode}
            showAllSportsChip={showAllSportsChip}
            onSportToggle={handleSportToggle}
            onAllSportsClick={handleAllSportsClick}
            onMarketTypeChange={setSelectedMarketType}
            onDateChange={setSelectedDate}
            onDatePrev={handleDatePrev}
            onDateNext={handleDateNext}
            onDateFilterChange={setDateFilter}
            onTodayClick={handleTodayClick}
            onStatusFilterChange={setStatusFilter}
            onMarketSideChange={setMarketSide}
            onPriceModeChange={setPriceMode}
          />
        </div>
        <div className="border-border w-96 shrink-0 border-l">
          <PositionsWidget
            expanded={showPositionSidebar}
            onToggle={() => setShowPositionSidebar((v) => !v)}
          />
        </div>
      </div>

      {/* Content row — game table + detail panel side-by-side, both below toolbar */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto p-4">
          {isMultiOutcomeSport ? (
            multiEvents.length > 0 ? (
              <>
                {usingMultiUpcomingFallback && (
                  <div className="border-blue-500/30 bg-blue-500/10 text-blue-200 light:text-blue-700 mb-3 rounded-md border px-3 py-2 text-xs">
                    No {sportFilterLabel} games on {selectedDate}.{' '}
                    {multiUpcomingFallbackDate
                      ? `Showing the next day with games — ${isoToMdy(multiUpcomingFallbackDate)}.`
                      : 'Showing the next day with games.'}
                  </div>
                )}
                <MultiOutcomeTable
                  events={multiEvents}
                  titleLabel={tableTitleLabel}
                  seriesTicker={seriesTicker}
                  marketSide={marketSide}
                  priceMode={priceMode}
                  expandedOutcomeKey={expandedGameKey}
                  onOutcomeClick={handleMultiOutcomeClick}
                  positionByTicker={userActivity.positionByTicker}
                  ordersByTicker={userActivity.ordersByTicker}
                />
              </>
            ) : (
              <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                {connected ? (
                  <span>No {sportFilterLabel} events</span>
                ) : (
                  <>
                    <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                    <span>Connecting to Hydra...</span>
                  </>
                )}
              </div>
            )
          ) : selectedMarketType === 'series' ? (
            seriesEvent ? (
              <SeriesTable event={seriesEvent} />
            ) : (
              <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                {connected ? (
                  <span>No {selectedSport.toUpperCase()} series markets</span>
                ) : (
                  <>
                    <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                    <span>Connecting to Hydra...</span>
                  </>
                )}
              </div>
            )
          ) : selectedMarketType === 'spread-total' ? (
            <SpreadsTotals
              sport={selectedSport}
              statusFilter={statusFilter}
              dateFilter={dateFilter}
              selectedDateIso={selectedDateIso}
              marketSide={marketSide}
              priceMode={priceMode}
              onActiveGameChange={setSpreadTotalGame}
              onActiveBetLineContextChange={setSpreadTotalBetCtx}
              onTradeClick={(data) => {
                // Side panel already opened via onActiveGameChange; this
                // hand-off carries the click's pre-fill (price + side)
                // into the ticket via tradeIntent. The synthesized
                // GameRow has identical away/home books so `teamSide`
                // collapses to 'away' here.
                setTradeIntent({
                  side: data.teamSide,
                  venue: data.venue,
                  prefillPrice: data.prefillPrice,
                  initialOrderSide: data.initialOrderSide,
                  postOnly: data.postOnly,
                  // Tag the prefill with the line it came from. Selecting a
                  // different event swaps the panel's game without a fresh
                  // click; the stale price is then ignored so the ticket
                  // seeds the new book's best bid. See TradeIntent.gameKey.
                  gameKey: data.game.key,
                });
                // Page-level YES/NO toggle follows the pill the trader
                // just clicked — green YES pill flips the toolbar to
                // YES so subsequent reads are consistent.
                if (data.initialOrderSide && data.initialOrderSide !== marketSide) {
                  setMarketSide(data.initialOrderSide);
                }
              }}
              closeSignal={stCloseSignal}
            />
          ) : games.length > 0 || archivedLoading ? (
            <>
              {usingUpcomingFallback && (
                <div className="border-blue-500/30 bg-blue-500/10 text-blue-200 light:text-blue-700 mb-3 rounded-md border px-3 py-2 text-xs">
                  No {sportFilterLabel} games on {selectedDate}.{' '}
                  {upcomingFallbackDate
                    ? `Showing the next day with games — ${isoToMdy(upcomingFallbackDate)}.`
                    : 'Showing the next day with games.'}
                </div>
              )}
              <GameTable
                games={games}
                titleLabel={tableTitleLabel}
                seriesTicker={seriesTicker}
                marketType={selectedMarketType}
                marketSide={marketSide}
                priceMode={priceMode}
                expandedGameKey={expandedGameKey}
                expandedTeamSide={tradeIntent?.side ?? null}
                onToggleExpand={handleToggleExpand}
                onTradeClick={handleTradeClick}
                positionByTicker={userActivity.positionByTicker}
                ordersByTicker={userActivity.ordersByTicker}
                botPresenceByTicker={edgeBots.byTicker}
                positionGameKeys={positionGameKeys}
                archivedLoading={archivedLoading}
              />
            </>
          ) : (
            <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
              {connected ? (
                <span>No {sportFilterLabel} games</span>
              ) : (
                <>
                  <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                  <span>Connecting to Hydra...</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Detail panel — sibling of the table, both below the toolbar.
         *  Key off the active game so switching between moneyline and
         *  spread/total expansions remounts the panel cleanly. */}
        {expandedGame && (
          <ExpandedGamePanel
            game={expandedGame}
            onClose={handleCloseDetail}
            tradeIntent={tradeIntent}
            marketSide={marketSide}
            onTeamSideChange={(side) => {
              setTradeIntent((t) => (t ? { ...t, side } : null));
            }}
            betLineContext={
              // Identity check for spread/total (the synth is pushed and held
              // by reference); multi-outcome synths are re-derived fresh each
              // tick, so match on the key instead.
              spreadTotalGame === expandedGame || expandedGame.key.includes('|multi|')
                ? spreadTotalBetCtx
                : null
            }
          />
        )}
      </div>

      {/* The bottom bar (display toggles + Hydra feed inspection) was promoted
          to the global <StatusBar/> in AuthShell — content-width, on every
          screen, fused with the rate-limit/tier account cluster. */}

      {/* Position popup — fixed-position overlay, can coexist with the
          detail panel (used to fight it for the right rail). */}
      {showPositionSidebar && (
        <PositionSidebar
          onClose={() => setShowPositionSidebar(false)}
          onOpenDetail={
            onOpenPositionsPage
              ? () => {
                  setShowPositionSidebar(false);
                  onOpenPositionsPage();
                }
              : undefined
          }
        />
      )}

      {/* Trade modal removed 2026-05-22 — see TradeModal.tsx for the
          deprecation note. All trade entry now happens inside the
          GameDetailPanel side panel rendered above. */}
    </div>
  );
}

/** Thin wrapper around GameDetailPanel that pulls the Unabated
 *  consensus from the batched gameStates poll and threads it into the
 *  panel's `unabated` prop. Extracted so the `useGameStateFor` hook
 *  call has a stable mount lifetime — putting the hook directly in
 *  ValueDashboard would mean it runs on every render whether or not
 *  any panel is open. Same swap-by-team-code logic the GameDetail page
 *  uses: Unabated's away/home can disagree with the canonical alpha-
 *  sorted GameRow, so we align by team code before mapping.
 *  Exported: PositionTracker mounts the same panel as its right-rail
 *  buy window so both surfaces share one ticket + consensus wiring. */
export function ExpandedGamePanel({
  game,
  onClose,
  tradeIntent,
  marketSide,
  onTeamSideChange,
  betLineContext,
}: {
  game: GameRow;
  onClose: () => void;
  tradeIntent: TradeIntent | null;
  marketSide: MarketSide;
  onTeamSideChange: (side: TeamSide) => void;
  betLineContext: BetLineContext | null;
}) {
  // For spread/total synth keys (`<eventKey>|spread|<ticker>`) we want
  // the eventKey portion for the gameStates lookup, not the synth key
  // itself. Moneyline rows fall through unchanged.
  const lookupBase = (() => {
    const s = game.key.indexOf('|spread|');
    if (s >= 0) return game.key.slice(0, s);
    const t = game.key.indexOf('|total|');
    if (t >= 0) return game.key.slice(0, t);
    const m = game.key.indexOf('|multi|');
    if (m >= 0) return game.key.slice(0, m);
    return game.key;
  })();
  const hydraKey = toHydraGameKey(lookupBase);
  const gameState = useGameStateFor(hydraKey);
  // Prefer the live WS consensus (sub-second freshness via the `unabated`
  // channel, push on ≥1¢ shift). Fall back to the 5s gameStates poll
  // snapshot for cold-start and any game outside hydra's active consensus set.
  // Consensus is cached per the doubleheader-safe v2 key, which IS the row key,
  // so pass `lookupBase` (the row key minus any synth suffix) straight through —
  // a doubleheader's two games then read their own un-blended consensus.
  const consensusKey = lookupBase || null;
  const liveConsensus = useConsensusFor(consensusKey);
  const unabated = (() => {
    // WS is now the sole source of consensus — hydra moved it off the REST
    // gameStates response as of a37c761. Snapshot's durable last-known cache
    // fills in stable pre-game markets that don't actively shift.
    const c = liveConsensus;
    if (!c) return null;
    // Same swap rule the GameDetail page applies — Unabated's awayTeam
    // can land in our homeCode slot after canonical-key alpha-sort.
    // swapVenueColumns propagates the same flip to K and P rows so all
    // three columns line up under the matching team-code headers.
    const swapped = c.awayTeam === game.homeCode;
    return {
      awayCents: swapped ? c.homeFairProbCents : c.awayFairProbCents,
      homeCents: swapped ? c.awayFairProbCents : c.homeFairProbCents,
    };
  })();
  return (
    <GameDetailPanel
      key={game.key}
      game={game}
      onClose={onClose}
      tradeIntent={tradeIntent}
      marketSide={marketSide}
      onTeamSideChange={onTeamSideChange}
      betLineContext={betLineContext}
      unabated={unabated}
    />
  );
}

// Project an analytics archived game into the GameRow shape the table
// already consumes. Most fields are stub-empty — the renderer branches
// on `archived` and skips the live-row plumbing entirely. `key` is the
// canonical hydra gameKey (date|TEAM_A|TEAM_B), which is also what
// positionGameKeys is keyed on, so the archived row's position-highlight
// lookup works without any extra translation.
function archivedToGameRow(a: ArchivedGame): GameRow {
  return {
    key: a.gameKey,
    sport: a.sport as Sport,
    date: a.date,
    timePt: '',
    startTimeMs: null,
    gameState: 'final',
    finalWinnerSide: null,
    awayCode: a.teamA,
    homeCode: a.teamB,
    awayName: a.teamA,
    homeName: a.teamB,
    kalshi: null,
    polymarket: null,
    archived: {
      kalshiVolume: a.kalshiVolume,
      polyVolume: a.polyVolume,
    },
  };
}
